<template>
    <div class="update-creative form text-left w-100">
        <!-- Current Creative Display -->
        <div class="form-group">
            <creative-display
                v-if="displayCreative"
                :creative="displayCreative"
                class="mb-2"
            />
        </div>

        <!-- Reason for Change -->
        <div class="form-group mt-2">
            <label class="d-block">Reason for change</label>
            <span class="reason-text">{{ creative.status_reason }}</span>
        </div>

        <!-- Notes (if any) -->
        <div v-if="creative.status_notes" class="form-group">
            <label class="d-block">Notes</label>
            <span class="notes-text">{{ creative.status_notes }}</span>
        </div>

        <!-- Requirements / Upload Section -->
        <div class="form-group">
            <label class="d-block">Requirements</label>

            <!-- Image Creative -->
            <template v-if="isImage">
                <p class="mt-2"><strong>Type of Media:</strong> Image</p>
                <p v-if="creative.spec_width && creative.spec_height">
                    Dimensions: {{ creative.spec_width }}px (width) x {{ creative.spec_height }}px (height)
                </p>
                <t-file
                    :id="uploaderId"
                    :file-types="['image']"
                    :multiple="false"
                    :attach-to="attachTo"
                    @uploaded="onFileUploaded"
                />
            </template>

            <!-- Audio/Video Creative -->
            <template v-else-if="isAudioVideo">
                <p class="mt-2 mb-4"><strong>Type of Media:</strong> Audio/Video</p>
                <t-file
                    :id="uploaderId"
                    :file-types="['video', 'audio']"
                    :multiple="false"
                    :attach-to="attachTo"
                    @uploaded="onFileUploaded"
                />
            </template>

            <!-- Text Creative -->
            <template v-else-if="isText">
                <p class="mt-2 mb-4"><strong>Type of Media:</strong> Text</p>
                <div class="form-group">
                    <h4>Copy/Text</h4>
                    <textarea
                        v-model="text"
                        class="form-control"
                        rows="4"
                        placeholder="Enter your text here..."
                    ></textarea>
                </div>
            </template>
        </div>

        <!-- Footer Buttons -->
        <div class="modal-footer">
            <button
                class="btn btn-secondary fixed-width ml-auto"
                :disabled="!canSave || saving"
                @click="save"
            >
                <span v-if="saving">
                    <i class="fa fa-spinner fa-spin"></i> Saving...
                </span>
                <span v-else>Save</span>
            </button>
            <button
                v-if="showDoneButton"
                id="doneButton"
                class="btn btn-primary fixed-width"
                @click="done"
            >
                Done
            </button>
        </div>
    </div>
</template>

<script>
import CreativeTableApi from '@js/services/CreativeTableApi';

export default {
    name: 'UpdateCreative',

    props: {
        /**
         * Creative object with all necessary properties.
         */
        creative: {
            type: Object,
            required: true,
        },

        /**
         * Whether to show the Done button.
         */
        showDoneButton: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            text: this.creative.text || '',
            originalText: this.creative.text || '',
            hasReplacementMedia: false,
            saving: false,
            uploadedMedia: null,
        };
    },

    computed: {
        /**
         * Creative type checks.
         */
        isImage() {
            return this.creative.creative_type === 'image';
        },

        isAudioVideo() {
            return this.creative.creative_type === 'audio_video';
        },

        isText() {
            return this.creative.creative_type === 'text';
        },

        /**
         * Unique ID for the file uploader.
         */
        uploaderId() {
            return `update-creative-uploader-${this.creative.id}`;
        },

        /**
         * Attach-to configuration for file upload.
         */
        attachTo() {
            return {
                model_name: 'App\\Creative',
                model_id: this.creative.id,
                collection_name: 'replace_creative',
            };
        },

        /**
         * Whether changes can be saved.
         */
        canSave() {
            if (this.isText) {
                return this.text !== this.originalText;
            }
            return this.hasReplacementMedia;
        },

        /**
         * Creative object for display - shows replacement media if uploaded.
         */
        displayCreative() {
            if (this.uploadedMedia) {
                return {
                    ...this.creative,
                    media_url: this.uploadedMedia.url,
                    media: this.uploadedMedia,
                };
            }
            return this.creative;
        },
    },

    watch: {
        /**
         * Reset state when creative changes.
         */
        creative: {
            handler(newCreative) {
                this.text = newCreative.text || '';
                this.originalText = newCreative.text || '';
                this.hasReplacementMedia = false;
                this.uploadedMedia = null;
            },
            deep: true,
        },
    },

    methods: {
        /**
         * Handle file upload completion.
         */
        onFileUploaded(response) {
            this.hasReplacementMedia = true;
            this.uploadedMedia = response;
            this.$emit('file-uploaded', response);
        },

        /**
         * Save the updated creative.
         */
        async save() {
            this.saving = true;

            try {
                const data = {};

                if (this.isText) {
                    data.text = this.text;
                }

                await CreativeTableApi.applyChange(this.creative.id, data);

                // Update original values
                this.originalText = this.text;
                this.hasReplacementMedia = false;

                this.$emit('saved');
                Bus.$emit('reloadSidebar');
                Bus.$emit('toast', 'Creative updated successfully.', 'success');
            } catch (error) {
                Bus.$emit('toast', 'Failed to save creative.', 'error');
            } finally {
                this.saving = false;
            }
        },

        /**
         * Emit done event to close the modal.
         */
        done() {
            this.$emit('done');
            Bus.$emit('closeReviewCreative');
        },
    },
};
</script>

<style scoped>
.update-creative {
    padding: 10px;
}

.form-group {
    margin-bottom: 1rem;
}

.form-group label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 600;
}

.reason-text,
.notes-text {
    display: block;
    color: #333;
}

.form-control {
    display: block;
    width: 100%;
    padding: 0.375rem 0.75rem;
    font-size: 1rem;
    line-height: 1.5;
    color: #495057;
    background-color: #fff;
    border: 1px solid #ced4da;
    border-radius: 0.25rem;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.form-control:focus {
    border-color: #80bdff;
    outline: 0;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.modal-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 1rem 0;
    border-top: 1px solid #dee2e6;
    gap: 0.5rem;
}

.btn {
    padding: 0.375rem 0.75rem;
    font-size: 1rem;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.15s ease-in-out;
}

.btn-secondary {
    background-color: #6c757d;
    color: #fff;
    border: 1px solid #6c757d;
}

.btn-secondary:hover:not(:disabled) {
    background-color: #5a6268;
    border-color: #545b62;
}

.btn-primary {
    background-color: #007bff;
    color: #fff;
    border: 1px solid #007bff;
}

.btn-primary:hover:not(:disabled) {
    background-color: #0069d9;
    border-color: #0062cc;
}

.btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

.fixed-width {
    min-width: 80px;
}
</style>
